import localBaseBlankPlateConfig from '../data/lisonex-base-blank-plate.config.json';
import localBentSlotLinkConfig from '../data/lisonex-bent-slot-link.config.json';
import localMachines from '../data/lisonex-machines.json';
import localUpinkaConfig from '../data/lisonex-upinka-01-three.config.json';

type ProductConfiguratorConfig = typeof localBentSlotLinkConfig;
type ConfiguratorMachine = (typeof localMachines)[number];
type ConfiguratorReference = {
  id?: number | string;
  identifier?: string;
  slug?: string;
  token?: string;
};
type ConfiguratorPayloadConfig = ProductConfiguratorConfig & {
  machines?: ConfiguratorMachine[];
  relatedProducts?: ProductWithDefaults[];
};
export type StoreDashboardConfiguration = {
  id?: number | string;
  token?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  total_price?: string | number | null;
  total_price_with_vat?: string | number | null;
  currency?: string | null;
  configurator?: {
    id?: number | string;
    name?: string | Record<string, string | null | undefined> | null;
    slug?: string;
    renderer?: string;
  } | null;
  version?: string | number | null;
  payload?: Record<string, unknown> | null;
  customer_data?: Record<string, unknown> | null;
  calculation?: Record<string, unknown> | null;
  configuration_output?: Record<string, unknown> | null;
};

export type ConfiguratorData = ProductConfiguratorConfig & {
  machines: ConfiguratorMachine[];
};
export type RuntimeConfiguratorData = ConfiguratorData & {
  productConfigs?: ConfiguratorData[];
  relatedProducts?: ProductWithDefaults[];
};

const LOCAL_PRODUCT_CONFIGS: ProductConfiguratorConfig[] = [
  localBentSlotLinkConfig as ProductConfiguratorConfig,
  localUpinkaConfig as ProductConfiguratorConfig,
  localBaseBlankPlateConfig as ProductConfiguratorConfig,
];

export const LOCAL_CONFIGURATOR_DATA: ConfiguratorData = withMachines(
  localBentSlotLinkConfig as ProductConfiguratorConfig,
);
export const LOCAL_CONFIGURATOR_CONFIGS: ConfiguratorData[] = LOCAL_PRODUCT_CONFIGS.map(config =>
  withMachines(config),
);

let runtimeConfiguratorData: RuntimeConfiguratorData = getDefaultRuntimeConfiguratorData();

export function getConfiguratorData() {
  return runtimeConfiguratorData;
}

export function setConfiguratorData(
  data: RuntimeConfiguratorData | ConfiguratorData | null | undefined,
) {
  runtimeConfiguratorData = data
    ? normalizeConfiguratorData(data)
    : getDefaultRuntimeConfiguratorData();
}

export function getProductConfiguratorConfigs() {
  if (Array.isArray(runtimeConfiguratorData.productConfigs)) {
    return runtimeConfiguratorData.productConfigs;
  }

  return [runtimeConfiguratorData as ConfiguratorData];
}

export function getConfiguratorDataForProduct(productTypeId?: string) {
  const configs = getProductConfiguratorConfigs();
  if (!productTypeId) return runtimeConfiguratorData;

  return configs.find(config => config.product.id === productTypeId) ?? runtimeConfiguratorData;
}

export function getConfiguratorConfigSource() {
  return (
    process.env.NEXT_PUBLIC_CONFIGURATOR_CONFIG_SOURCE?.trim() ||
    process.env.CONFIGURATOR_CONFIG_SOURCE?.trim() ||
    'backend'
  ).toLowerCase();
}

export function shouldUseLocalConfiguratorConfig() {
  return getConfiguratorConfigSource() === 'local';
}

function shouldExpandLegacyRelatedProducts() {
  return (
    (
      process.env.NEXT_PUBLIC_CONFIGURATOR_EXPAND_RELATED_PRODUCTS?.trim() ||
      process.env.CONFIGURATOR_EXPAND_RELATED_PRODUCTS?.trim() ||
      ''
    ).toLowerCase() === 'true'
  );
}

function getConfiguredEndpoint() {
  return (
    process.env.NEXT_PUBLIC_CONFIGURATOR_CONFIG_URL?.trim() ||
    process.env.CONFIGURATOR_CONFIG_URL?.trim() ||
    '/store/product-configurators?results=100'
  );
}

function getConfiguredMachinesEndpoint() {
  return (
    process.env.NEXT_PUBLIC_CONFIGURATOR_MACHINES_URL?.trim() ||
    process.env.CONFIGURATOR_MACHINES_URL?.trim() ||
    '/store/product-configurators/machines'
  );
}

function getConfiguredDetailSlugs() {
  const rawSlugs =
    process.env.NEXT_PUBLIC_CONFIGURATOR_DETAIL_SLUGS?.trim() ||
    process.env.CONFIGURATOR_DETAIL_SLUGS?.trim() ||
    '';

  return rawSlugs
    .split(',')
    .map(slug => slug.trim())
    .filter(Boolean);
}

function buildUrl(endpoint: string, locale?: string) {
  if (!endpoint) return null;

  const baseUrl = process.env.NEXT_PUBLIC_API_URL ?? process.env.NEXT_PUBLIC_APP_URL;
  const url =
    endpoint.startsWith('http://') || endpoint.startsWith('https://')
      ? new URL(endpoint)
      : new URL(endpoint.replace(/^\/api\//, '/'), getApiBaseUrl(baseUrl));

  if (locale && !url.searchParams.has('locale')) {
    url.searchParams.set('locale', locale);
  }

  return url.toString();
}

function getApiBaseUrl(baseUrl?: string) {
  const fallbackBaseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const normalizedBaseUrl = (baseUrl || fallbackBaseUrl).replace(/\/+$/, '');
  return `${normalizedBaseUrl.replace(/\/api$/, '')}/`;
}

function buildConfiguratorConfigUrl(locale?: string) {
  return buildUrl(getConfiguredEndpoint(), locale);
}

function buildLegacyConfiguratorConfigUrl(locale?: string) {
  const legacyEndpoint =
    process.env.NEXT_PUBLIC_CONFIGURATOR_LEGACY_CONFIG_URL?.trim() ||
    process.env.CONFIGURATOR_LEGACY_CONFIG_URL?.trim() ||
    '';

  return legacyEndpoint ? buildUrl(legacyEndpoint, locale) : null;
}

function buildStoreConfiguratorSlugUrl(slug: string, locale?: string) {
  return buildUrl(`/store/product-configurators/${encodeURIComponent(slug)}`, locale);
}

function buildStoreConfigurationDetailUrl(
  reference: ConfiguratorReference,
  locale?: string,
  basePath = '/store/product-configurators',
) {
  const isProductConfiguratorPath = basePath.includes('/product-configurators');
  const path =
    isProductConfiguratorPath && reference.slug
      ? `${basePath}/${encodeURIComponent(reference.slug)}`
      : reference.identifier
        ? `${basePath}/identifier/${encodeURIComponent(reference.identifier)}`
        : reference.slug
          ? `${basePath}/${encodeURIComponent(reference.slug)}`
          : reference.token
            ? `${basePath}/${encodeURIComponent(reference.token)}`
            : reference.id
              ? `${basePath}/${encodeURIComponent(String(reference.id))}`
              : '';

  return buildUrl(path, locale);
}

function buildConfiguratorMachinesUrl(locale?: string) {
  return buildUrl(getConfiguredMachinesEndpoint(), locale);
}

function buildDashboardConfigurationsUrl(locale?: string) {
  return buildUrl('/store/product-configurators/configurations', locale);
}

function getFallbackMachines() {
  return localMachines as ConfiguratorMachine[];
}

function getMachineFallbackForCurrentSource() {
  return shouldUseLocalConfiguratorConfig() ? getFallbackMachines() : [];
}

function withMachines(
  config: ProductConfiguratorConfig | ConfiguratorPayloadConfig,
  machines = getMachineFallbackForCurrentSource(),
) {
  const configMachines = Array.isArray((config as { machines?: unknown }).machines)
    ? ((config as { machines?: ConfiguratorMachine[] }).machines ?? [])
    : [];
  const resolvedMachines = shouldUseLocalConfiguratorConfig()
    ? configMachines.length
      ? configMachines
      : machines
    : machines;

  return {
    ...config,
    machines: resolvedMachines,
  } as ConfiguratorData;
}

function getLocalConfiguratorData(machines = getFallbackMachines()) {
  return normalizeConfiguratorDataList(LOCAL_PRODUCT_CONFIGS, machines);
}

function getDefaultRuntimeConfiguratorData(machines = getFallbackMachines()) {
  return shouldUseLocalConfiguratorConfig()
    ? getLocalConfiguratorData(machines)
    : getEmptyBackendConfiguratorData(machines);
}

function getEmptyBackendConfiguratorData(
  machines = getFallbackMachines(),
): RuntimeConfiguratorData {
  return {
    ...withMachines(localBentSlotLinkConfig as ProductConfiguratorConfig, machines),
    relatedProducts: [],
    productConfigs: [],
  };
}

function looksLikeConfiguratorData(value: unknown): value is ConfiguratorPayloadConfig {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Record<string, unknown>;

  return Boolean(
    candidate.product &&
    candidate.defaults &&
    candidate.limits &&
    candidate.materials &&
    candidate.finishes,
  );
}

function looksLikeMachine(value: unknown): value is ConfiguratorMachine {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Record<string, unknown>;
  return Boolean(candidate.id && candidate.name && candidate.process);
}

function normalizeMachine(value: unknown): ConfiguratorMachine | null {
  if (looksLikeMachine(value)) return value;
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Record<string, unknown>;
  const data =
    candidate.data && typeof candidate.data === 'object'
      ? (candidate.data as Record<string, unknown>)
      : {};
  const images = Array.isArray(candidate.images) ? candidate.images : [];
  const firstImage = images.find(image => image && typeof image === 'object') as
    Record<string, unknown> | undefined;
  const name =
    typeof candidate.name === 'string'
      ? candidate.name
      : typeof data.name === 'string'
        ? data.name
        : '';
  const process =
    typeof data.process === 'string'
      ? data.process
      : typeof candidate.process === 'string'
        ? candidate.process
        : 'laser';
  const id =
    typeof data.id === 'string' || typeof data.id === 'number'
      ? data.id
      : typeof data.key === 'string' || typeof data.key === 'number'
        ? data.key
        : typeof data.slug === 'string' || typeof data.slug === 'number'
          ? data.slug
          : candidate.id;

  if (!id || !name) return null;

  return {
    id: String(id),
    name,
    description: (typeof data.description === 'string'
      ? data.description
      : typeof candidate.description === 'string'
        ? candidate.description
        : ''
    )
      .replace(/<[^>]*>/g, '')
      .trim(),
    image:
      typeof data.image === 'string'
        ? data.image
        : typeof firstImage?.url === 'string'
          ? firstImage.url
          : typeof firstImage?.path === 'string'
            ? firstImage.path
            : (getFallbackMachines()[0]?.image ?? ''),
    process,
    supportedOperations: Array.isArray(data.supportedOperations ?? data.supported_operations)
      ? ((data.supportedOperations ??
          data.supported_operations) as ConfiguratorMachine['supportedOperations'])
      : ([process] as ConfiguratorMachine['supportedOperations']),
    supportedThicknessMm: Array.isArray(data.supportedThicknessMm ?? data.supported_thickness_mm)
      ? ((data.supportedThicknessMm ??
          data.supported_thickness_mm) as ConfiguratorMachine['supportedThicknessMm'])
      : (getFallbackMachines()[0]?.supportedThicknessMm ?? [1, 60]),
    setupPrice: Number(data.setupPrice ?? data.setup_price ?? 0),
    cutPricePerMeter: Number(data.cutPricePerMeter ?? data.cut_price_per_meter ?? 0),
    holeCutPrice: Number(data.holeCutPrice ?? data.hole_cut_price ?? 0),
    bendPrice: Number(data.bendPrice ?? data.bend_price ?? 0),
  } as ConfiguratorMachine;
}

type ProductWithDefaults = ConfiguratorData['product'] & {
  defaults?: unknown;
};

function stripProductDefaults(product: ProductWithDefaults): ConfiguratorData['product'] {
  const productOnly = { ...product };
  delete (productOnly as { defaults?: unknown }).defaults;

  return productOnly as ConfiguratorData['product'];
}

function withConfiguratorSlug(
  config: ConfiguratorPayloadConfig,
  slug?: string,
): ConfiguratorPayloadConfig {
  if (!slug) return config;

  return {
    ...config,
    product: {
      ...config.product,
      configuratorSlug: slug,
      slug: (config.product as { slug?: string }).slug ?? slug,
    } as ConfiguratorPayloadConfig['product'],
  };
}

function buildRelatedProductConfig(baseConfig: ConfiguratorData, product: ProductWithDefaults) {
  return {
    ...baseConfig,
    product: stripProductDefaults(product),
    defaults: (product.defaults ?? baseConfig.defaults) as ConfiguratorData['defaults'],
    relatedProducts: [],
  } as ConfiguratorData;
}

function expandLegacyRelatedProducts(config: ConfiguratorData) {
  const relatedProducts = Array.isArray((config as { relatedProducts?: unknown }).relatedProducts)
    ? ((config as { relatedProducts?: ProductWithDefaults[] }).relatedProducts ?? [])
    : [];
  const primaryConfig = {
    ...config,
    relatedProducts: [],
  } as ConfiguratorData;

  return [
    primaryConfig,
    ...relatedProducts.map(product => buildRelatedProductConfig(config, product)),
  ];
}

function uniqueProductConfigs(configs: ConfiguratorData[]) {
  const order: string[] = [];
  const byId = new Map<string, ConfiguratorData>();
  const getSlug = (config: ConfiguratorData) => {
    const product = config.product as { configuratorSlug?: unknown; slug?: unknown };
    return typeof product.configuratorSlug === 'string'
      ? product.configuratorSlug
      : typeof product.slug === 'string'
        ? product.slug
        : '';
  };

  configs.forEach(config => {
    const id = String(config.product.id);
    const existing = byId.get(id);

    if (!existing) {
      order.push(id);
      byId.set(id, config);
      return;
    }

    if (!getSlug(existing) && getSlug(config)) {
      byId.set(id, config);
    }
  });

  return order
    .map(id => byId.get(id))
    .filter((config): config is ConfiguratorData => Boolean(config));
}

function normalizeConfiguratorDataList(
  configs: ConfiguratorPayloadConfig[],
  machines = getFallbackMachines(),
): RuntimeConfiguratorData {
  const configsWithMachines = configs.map(config => withMachines(config, machines));
  const expandedConfigs = uniqueProductConfigs(
    shouldExpandLegacyRelatedProducts()
      ? configsWithMachines.flatMap(expandLegacyRelatedProducts)
      : configsWithMachines.map(
          config =>
            ({
              ...config,
              relatedProducts: [],
            }) as ConfiguratorData,
        ),
  );
  const [primaryConfig] = expandedConfigs;
  if (!primaryConfig) return getEmptyBackendConfiguratorData(machines);

  const productOptions = expandedConfigs.map(config => ({
    ...config.product,
    defaults: config.defaults,
  }));

  return {
    ...primaryConfig,
    product: productOptions[0] as ConfiguratorData['product'],
    relatedProducts: productOptions.slice(1) as ProductWithDefaults[],
    productConfigs: expandedConfigs,
  } as RuntimeConfiguratorData;
}

export function normalizeConfiguratorData(data: RuntimeConfiguratorData | ConfiguratorData) {
  const productConfigs = (data as RuntimeConfiguratorData).productConfigs;
  if (Array.isArray(productConfigs)) {
    if (productConfigs.length) return normalizeConfiguratorDataList(productConfigs, data.machines);

    return {
      ...withMachines(data as ConfiguratorPayloadConfig, data.machines),
      relatedProducts: [],
      productConfigs: [],
    } as RuntimeConfiguratorData;
  }

  return normalizeConfiguratorDataList([data as ConfiguratorPayloadConfig], data.machines);
}

function extractConfiguratorDataList(payload: unknown, depth = 0): ConfiguratorPayloadConfig[] {
  if (depth > 4) return [];
  if (looksLikeConfiguratorData(payload)) return [payload];

  if (Array.isArray(payload)) {
    return payload.flatMap(item => extractConfiguratorDataList(item, depth + 1));
  }

  if (typeof payload === 'string') {
    try {
      return extractConfiguratorDataList(JSON.parse(payload), depth + 1);
    } catch {
      return [];
    }
  }

  if (!payload || typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;
  const slug = typeof record.slug === 'string' ? record.slug : undefined;
  const nested =
    record.data ??
    record.schema ??
    record.content ??
    record.attributes ??
    record.items ??
    record.records ??
    record.products ??
    record.configs ??
    record.configurations ??
    record.config ??
    record.configuration ??
    record.configurationJson ??
    record.configuration_json ??
    record.configurator ??
    record.configuratorConfig ??
    record.configurator_config ??
    record.configuratorConfigJson ??
    record.configurator_config_json ??
    record.productConfig ??
    record.product_config ??
    record.productConfigJson ??
    record.product_config_json ??
    record.config_json ??
    record.json ??
    record.value ??
    null;

  const configs = extractConfiguratorDataList(nested, depth + 1);
  return slug ? configs.map(config => withConfiguratorSlug(config, slug)) : configs;
}

function extractConfigurationReferences(payload: unknown, depth = 0): ConfiguratorReference[] {
  if (depth > 4 || !payload) return [];

  if (Array.isArray(payload)) {
    return payload.flatMap(item => extractConfigurationReferences(item, depth + 1));
  }

  if (typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;
  const references: ConfiguratorReference[] = [];
  const id = typeof record.id === 'string' || typeof record.id === 'number' ? record.id : undefined;
  const identifier = typeof record.identifier === 'string' ? record.identifier : undefined;
  const slug = typeof record.slug === 'string' ? record.slug : undefined;
  const token = typeof record.token === 'string' ? record.token : undefined;

  if (id || identifier || slug || token) {
    references.push({ id, identifier, slug, token });
  }

  const nested =
    record.data ??
    record.items ??
    record.records ??
    record.configurations ??
    record.configs ??
    null;

  return [...references, ...extractConfigurationReferences(nested, depth + 1)];
}

function extractMachinesPayload(payload: unknown, depth = 0): ConfiguratorMachine[] {
  if (depth > 4) return [];

  if (Array.isArray(payload)) {
    const machines = payload
      .map(item => normalizeMachine(item))
      .filter((machine): machine is ConfiguratorMachine => Boolean(machine));
    if (machines.length === payload.length) return machines;
    return [...machines, ...payload.flatMap(item => extractMachinesPayload(item, depth + 1))];
  }

  if (typeof payload === 'string') {
    try {
      return extractMachinesPayload(JSON.parse(payload), depth + 1);
    } catch {
      return [];
    }
  }

  if (!payload || typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;
  const nested =
    record.machines ??
    record.data ??
    record.items ??
    record.records ??
    record.configuratorMachines ??
    record.configurator_machines ??
    record.machineOptions ??
    record.machine_options ??
    null;

  return extractMachinesPayload(nested, depth + 1);
}

function unwrapConfiguratorDataPayload(
  payload: unknown,
  machines = getFallbackMachines(),
): RuntimeConfiguratorData | null {
  const configs = extractConfiguratorDataList(payload);
  if (!configs.length) return null;

  const payloadMachines = extractMachinesPayload(payload);
  return normalizeConfiguratorDataList(
    configs,
    payloadMachines.length ? payloadMachines : machines,
  );
}

async function loadConfiguratorMachines(locale?: string) {
  const fallbackMachines = getMachineFallbackForCurrentSource();
  const url = (() => {
    try {
      return buildConfiguratorMachinesUrl(locale);
    } catch {
      return null;
    }
  })();

  if (!url) return fallbackMachines;

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Current-Store': process.env.NEXT_PUBLIC_CURRENT_STORE ?? '',
        ...getEliadminTokenHeader(),
        ...(locale ? { 'Accept-Language': locale } : {}),
      },
    });

    if (!response.ok) return fallbackMachines;

    const machines = extractMachinesPayload(await response.json());
    return machines.length ? machines : fallbackMachines;
  } catch {
    return fallbackMachines;
  }
}

function getEliadminTokenHeader(): Record<string, string> {
  const token = process.env.ELIADMIN_TOKEN?.trim();
  if (!token) return {};

  return {
    'Eliadmin-Token': Buffer.from(token).toString('base64'),
  };
}

async function fetchConfiguratorJson(url: string, locale?: string) {
  const response = await fetch(url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Current-Store': process.env.NEXT_PUBLIC_CURRENT_STORE ?? '',
      ...getEliadminTokenHeader(),
      ...(locale ? { 'Accept-Language': locale } : {}),
    },
  });

  if (!response.ok) return null;

  return response.json();
}

export async function loadDashboardConfigurations(
  locale?: string,
  authToken?: string | null,
): Promise<StoreDashboardConfiguration[]> {
  if (!authToken) return [];

  const url = (() => {
    try {
      return buildDashboardConfigurationsUrl(locale);
    } catch {
      return null;
    }
  })();

  if (!url) return [];

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${authToken}`,
        'Current-Store': process.env.NEXT_PUBLIC_CURRENT_STORE ?? '',
        ...(locale ? { 'Accept-Language': locale } : {}),
      },
    });

    if (!response.ok) return [];

    const payload = (await response.json()) as { data?: unknown };
    return Array.isArray(payload.data) ? (payload.data as StoreDashboardConfiguration[]) : [];
  } catch {
    return [];
  }
}

async function loadConfigurationDetails(
  payload: unknown,
  machines: ConfiguratorMachine[],
  locale?: string,
  detailBasePath = '/store/product-configurators',
) {
  const references = extractConfigurationReferences(payload);
  const uniqueReferences = references.filter((reference, index, all) => {
    const key = reference.slug ?? reference.identifier ?? reference.token ?? reference.id;
    return (
      key &&
      all.findIndex(item => (item.slug ?? item.identifier ?? item.token ?? item.id) === key) ===
        index
    );
  });

  const detailPayloads = await Promise.all(
    uniqueReferences.map(async reference => {
      const detailUrl = (() => {
        try {
          return buildStoreConfigurationDetailUrl(reference, locale, detailBasePath);
        } catch {
          return null;
        }
      })();

      const payload = detailUrl
        ? await fetchConfiguratorJson(detailUrl, locale).catch(() => null)
        : null;
      return { payload, slug: reference.slug };
    }),
  );

  const configs = detailPayloads.flatMap(payloadItem =>
    extractConfiguratorDataList(payloadItem.payload).map(config =>
      withConfiguratorSlug(config, payloadItem.slug),
    ),
  );

  return configs.length ? normalizeConfiguratorDataList(configs, machines) : null;
}

async function loadConfiguratorSlugDetails(machines: ConfiguratorMachine[], locale?: string) {
  const slugs = getConfiguredDetailSlugs();
  if (!slugs.length) return null;

  const detailPayloads = await Promise.all(
    slugs.map(async slug => {
      const detailUrl = (() => {
        try {
          return buildStoreConfiguratorSlugUrl(slug, locale);
        } catch {
          return null;
        }
      })();

      const payload = detailUrl
        ? await fetchConfiguratorJson(detailUrl, locale).catch(() => null)
        : null;
      return { payload, slug };
    }),
  );

  const configs = detailPayloads.flatMap(payloadItem =>
    extractConfiguratorDataList(payloadItem.payload).map(config =>
      withConfiguratorSlug(config, payloadItem.slug),
    ),
  );

  return configs.length ? normalizeConfiguratorDataList(configs, machines) : null;
}

export async function loadConfiguratorData(locale?: string): Promise<RuntimeConfiguratorData> {
  const machines = await loadConfiguratorMachines(locale);
  if (shouldUseLocalConfiguratorConfig()) return getLocalConfiguratorData(machines);

  const urls = (() => {
    try {
      return [buildConfiguratorConfigUrl(locale), buildLegacyConfiguratorConfigUrl(locale)].filter(
        Boolean,
      ) as string[];
    } catch {
      return [];
    }
  })();
  if (!urls.length) return getEmptyBackendConfiguratorData(machines);

  try {
    for (const url of urls) {
      const payload = await fetchConfiguratorJson(url, locale);
      if (!payload) continue;
      const detailBasePath = url.includes('/store/configurations')
        ? '/store/configurations'
        : '/store/product-configurators';

      const configuratorData =
        unwrapConfiguratorDataPayload(payload, machines) ??
        (await loadConfigurationDetails(payload, machines, locale, detailBasePath));

      if (configuratorData) return configuratorData;
    }

    const detailSlugData = await loadConfiguratorSlugDetails(machines, locale);
    if (detailSlugData) return detailSlugData;

    return getEmptyBackendConfiguratorData(machines);
  } catch {
    return getEmptyBackendConfiguratorData(machines);
  }
}
